import React from "react";
import { SelectorDisplayItemProps } from "../components/Selector";
import { Fetcher } from "../components/Fetcher";
import { LogEvent } from "../models/LogEvents";
import { FilterContainer } from "../components/FilterContainer";
import { DEFAULT_PAGING_SIZE, MAX_TABLE_HEIGHT, MIN_TABLE_HEIGHT } from "../constants";
import { Pagination } from "../components/Pagination";
import { TwilioDetailsLogListContainer } from "../components/TwilioDetailsLogListContainer";
import { makeStyles } from "@material-ui/core/styles";
import { grey } from "@material-ui/core/colors";
import { MessageCard } from "../components/MessageCard";
import Typography from "@material-ui/core/Typography";
import { getTwilioAllLogsPage } from "../services/api/twilioAllLogs.api";
import { getTwilioWorkersList } from "../services/api/twilioWorkerList.api";
import queryString from "query-string";
import { store } from "../globalState/StateProvider";
import {
	getAllLogsFiltersLocalStorageValue,
	setAllLogsFiltersLocalStorageValue,
	resetAllLogsFiltersLocalStorageValue,
} from "../globalState/LocalStorage/TwilioLocalStorageAccessors";

interface AllTwilioLogsPageProps {
	// subscription id to fetch details
	subscriptionId: string;
	// product id used to fetch the subscription
	productId: string;
}

const useStyles = makeStyles((theme) => ({
	title: {
		marginBottom: theme.spacing(3),
	},
	tableWrapper: {
		minHeight: MIN_TABLE_HEIGHT,
		maxHeight: MAX_TABLE_HEIGHT,
		maxWidth: 1200,
		overflow: "auto",
		borderBottomColor: grey[100],
		borderBottomWidth: 1,
		borderBottomStyle: "solid",
	},
	tableTitle: {
		zIndex: 1,
	},
}));

const renderList = (
	events: LogEvent[],
	classes: Record<any, string>,
	setPageNumber: (n: number) => void,
	setRowsPerPage: (n: number) => void,
	pageNumber: number,
	rowsPerPage: number,
	subscriptionId: string,
	accountSid: string,
	productId: string,
) => {
	const query = queryString.parse(window.location.search);

	return (
		<div>
			{events.length > 0 || query.__sid !== undefined ? (
				<div>
					<div className={classes.tableWrapper}>
						<TwilioDetailsLogListContainer
							productId={productId}
							accountSid={accountSid}
							subscriptionId={subscriptionId}
							logs={events}
							pageName={"AllTwilioLogs"}
						></TwilioDetailsLogListContainer>
					</div>
					{events.length > 0 ? (
						<Pagination
							pageNumber={pageNumber}
							rowsPerPage={rowsPerPage}
							setPageFunction={setPageNumber}
							setRowsPerPageFunction={setRowsPerPage}
							currentPageLength={events.length}
						></Pagination>
					) : (
						<div></div>
					)}
				</div>
			) : (
				<MessageCard message="No Events found." />
			)}
		</div>
	);
};

export const AllTwilioLogsPage = (props: AllTwilioLogsPageProps) => {
	const classes = useStyles();
	const { environments, subscription } = React.useContext(store);

	const environmentsList =
		subscription && subscription!.subscription!.subscriptionId === props.subscriptionId ? environments : [];

	const [pageNumber, setPageNumber] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGING_SIZE);
	const [update, forceUpdate] = React.useState(false);

	const filterObject = getAllLogsFiltersLocalStorageValue(props.subscriptionId);

	function UpdateFilters(newFilters) {
		setAllLogsFiltersLocalStorageValue(props.subscriptionId, newFilters);
		forceUpdate(!update);
		setPageNumber(0);
	}

	function ResetFilters() {
		forceUpdate(!update);
		setPageNumber(0);
		return resetAllLogsFiltersLocalStorageValue(props.subscriptionId);
	}

	async function fetchWorkers(environment) {
		const response = await getTwilioWorkersList(props.productId, props.subscriptionId, environment);
		const newWorkers: SelectorDisplayItemProps[] = [
			{
				displayString: "Any",
				valueString: "Any",
			},
		];
		newWorkers.push(
			...response.data.map((item) => {
				return {
					displayString: item.friendlyName,
					valueString: item.workerSid,
				};
			}),
		);
		return newWorkers;
	}

	const GetAllLogsRequest = {
		startDate: filterObject.startDate,
		endDate: filterObject.endDate,
		requestedEventTypes: filterObject.eventTypeNames,
		requestedEventIds: filterObject.logLevelValues,
		searchSid: filterObject.searchSid,
		correlationToken: filterObject.correlationToken,
		pageNumber: pageNumber,
		pageSize: rowsPerPage,
		workerSid: filterObject.selectedWorker !== "Any" ? filterObject.selectedWorker : "",
	};

	function loadList() {
		if (environmentsList.length > 0) {
			return (
				<div>
					<Fetcher
						action={getTwilioAllLogsPage(
							GetAllLogsRequest,
							props.subscriptionId,
							props.productId,
							filterObject.selectedEnvironment !== ""
								? filterObject.selectedEnvironment
								: environmentsList[0].valueString!,
						)}
					>
						{(response) =>
							renderList(
								response.data,
								classes,
								setPageNumber,
								setRowsPerPage,
								pageNumber,
								rowsPerPage,
								props.subscriptionId,
								filterObject.selectedEnvironment !== ""
									? filterObject.selectedEnvironment
									: environmentsList[0].valueString!,
								props.productId,
							)
						}
					</Fetcher>
				</div>
			);
		}
	}

	function showFilters() {
		if (environmentsList.length > 0) {
			return (
				<FilterContainer
					fetchEnvironmentWorkers={fetchWorkers}
					selectedWorker={filterObject.selectedWorker}
					environmentFilter={{
						environments: environmentsList,
						selectedEnvironment:
							filterObject.selectedEnvironment !== ""
								? filterObject.selectedEnvironment
								: environmentsList[0].valueString!,
					}}
					dateFilter={{
						startDate: filterObject.startDate,
						endDate: filterObject.endDate,
						refreshDateTimespan: filterObject.refreshDateTimespan,
					}}
					eventTypeFilter={{
						selectedEventTypeNames: filterObject.eventTypeNames,
						eventTypeNames: [
							"taskRouter",
							"platformStatus",
							"runtimeDebugger",
							"browserFunction",
							"cloudFunction",
							"callEvent",
						],
					}}
					logLevelFilter={{
						selectedLogLevels: filterObject.logLevelValues,
						logLevelValues: [10000, 20000, 30000, 40000, 50000, 50012, 60000],
					}}
					searchSid={filterObject.searchSid}
					correlationToken={filterObject.correlationToken}
					onUpdate={UpdateFilters}
					onReset={ResetFilters}
				></FilterContainer>
			);
		}
	}

	return (
		<div data-testid={"subscription-all-twilio-logs-page"}>
			<Typography className={classes.title} variant="h4">
				All Logs
			</Typography>
			{showFilters()}
			<div style={{ margin: 10 }}></div>
			{loadList()}
		</div>
	);
};
